import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { authService } from '../../api/services';

export const loginUser = createAsyncThunk(
  'auth/login',
  async ({ email, password }, { rejectWithValue }) => {
    try {
      const { data } = await authService.login(email, password);
      localStorage.setItem('accessToken', data.access);
      localStorage.setItem('refreshToken', data.refresh);
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.detail || 'Login failed. Please check your credentials.'
      );
    }
  }
);

export const registerUser = createAsyncThunk(
  'auth/register',
  async (userData, { rejectWithValue }) => {
    try {
      const { data } = await authService.register(userData);
      localStorage.setItem('accessToken', data.tokens?.access);
      localStorage.setItem('refreshToken', data.tokens?.refresh);
      return data;
    } catch (error) {
      const msg =
        error.response?.data?.detail ||
        error.response?.data?.email?.[0] ||
        'Registration failed. Please try again.';
      return rejectWithValue(msg);
    }
  }
);

export const loadUser = createAsyncThunk(
  'auth/loadUser',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await authService.getProfile();
      return data;
    } catch (error) {
      return rejectWithValue('Failed to load user.');
    }
  }
);

export const updateUserProfile = createAsyncThunk(
  'auth/updateProfile',
  async (profileData, { rejectWithValue }) => {
    try {
      const { data } = await authService.updateProfile(profileData);
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.detail || 'Failed to update profile.'
      );
    }
  }
);

const readJSON = (key) => {
  try {
    return JSON.parse(localStorage.getItem(key));
  } catch {
    return null;
  }
};

const initialState = {
  user: null,
  token: localStorage.getItem('accessToken') || null,
  isAuthenticated: !!localStorage.getItem('accessToken'),
  loading: false,
  error: null,
  // Clinics the logged-in doctor works at + the one currently selected
  clinics: readJSON('clinics') || [],
  selectedClinic: readJSON('selectedClinic'),
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout: (state) => {
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      state.loading = false;
      state.error = null;
      state.clinics = [];
      state.selectedClinic = null;
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('clinics');
      localStorage.removeItem('selectedClinic');
    },
    selectClinic: (state, action) => {
      state.selectedClinic = action.payload;
      localStorage.setItem('selectedClinic', JSON.stringify(action.payload));
    },
    clearError: (state) => {
      state.error = null;
    },
    setCredentials: (state, action) => {
      state.user = action.payload.user;
      state.token = action.payload.token;
      state.isAuthenticated = true;
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.token = action.payload.access;
        const name = action.payload.name || '';
        const nameParts = name.trim().split(/\s+/);
        state.user = {
          id: action.payload.user_id,
          email: action.payload.email,
          name: name,
          first_name: action.payload.first_name || (nameParts[0] || ''),
          last_name: action.payload.last_name || (nameParts.slice(1).join(' ') || ''),
          role: action.payload.role,
          phone: action.payload.phone || '',
          // Laboratory access block (role, permissions, hospital).
          lab: action.payload.lab ?? null,
        };
        const clinics = action.payload.clinics || [];
        state.clinics = clinics;
        localStorage.setItem('clinics', JSON.stringify(clinics));
        // Always require a fresh clinic choice on login — the selection
        // decides which WhatsApp number the doctor's messages send from.
        state.selectedClinic = null;
        localStorage.removeItem('selectedClinic');
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Register
      .addCase(registerUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(registerUser.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.token = action.payload.access;
        state.user = action.payload.user;
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Load User
      .addCase(loadUser.pending, (state) => {
        state.loading = true;
      })
      .addCase(loadUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
        state.isAuthenticated = true;
      })
      .addCase(loadUser.rejected, (state) => {
        state.loading = false;
        state.isAuthenticated = false;
        state.token = null;
        state.user = null;
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
      })
      // Update Profile
      .addCase(updateUserProfile.fulfilled, (state, action) => {
        const updatedName =
          (action.payload.first_name || action.payload.last_name)
            ? `${action.payload.first_name || ''} ${action.payload.last_name || ''}`.trim()
            : (action.payload.name || state.user?.name);
        state.user = {
          ...state.user,
          ...action.payload,
          name: updatedName,
          role: action.payload.role || action.payload.user?.role || state.user?.role,
        };
      });
  },
});

export const { logout, clearError, setCredentials, selectClinic } = authSlice.actions;
export default authSlice.reducer;
